import { z } from 'zod';
import { APPOINTMENT_KEY_RE, NOTE_FORMATS, progressBodySchema, } from './clinical-notes.js';
/**
 * ehr#437 (FAMILY_COUPLES_NOTE_10-14-V15) - a family or couples session: the identified client
 * (billed: 90847, or 90846 when they weren't there) and the family members, each a contact in the
 * EHR with a note of their own on their own chart. The session holds the roster, Family or
 * Couples, and the session summary that is the same on every attendee's note.
 */
export const FAMILY_TYPES = ['Family', 'Couples'];
/** Start a session for a client (they're the first attendee, and the one billed). */
export const familyOpenSchema = z.object({
    clientId: z.string().uuid(),
    type: z.enum(FAMILY_TYPES),
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /**
     * calendar#139: the Calendar appointment it's for ("<eventId>@<YYYY-MM-DD>"). The session is tied
     * to it, so the Calendar's Progress Note (or + Add new on that appointment) reopens the same one.
     */
    appointmentKey: z.string().regex(APPOINTMENT_KEY_RE).optional(),
    durationMin: z.number().int().min(1).max(600).optional(),
});
export const familyUpdateSchema = z
    .object({
    type: z.enum(FAMILY_TYPES).optional(),
    sessionSummary: z.string().max(20000).optional(),
    /** ehr#689 F7 (V15): the shared "What happened in the session" notes for Generate everyone. */
    sharedNotes: z.string().max(20000).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update.' });
/** Add or take off attendees (contacts). The client stays; someone with a note stays. */
export const familyRosterSchema = z
    .object({
    add: z.array(z.string().uuid()).max(10).optional(),
    remove: z.array(z.string().uuid()).max(10).optional(),
})
    .refine((v) => (v.add?.length ?? 0) + (v.remove?.length ?? 0) > 0, {
    message: 'Add or remove at least one attendee.',
});
/** Save one attendee's note (created on the first save, on their own chart). */
export const familyNoteSaveSchema = z.object({
    date: z.coerce.date().optional(),
    format: z.enum(NOTE_FORMATS).optional(),
    body: progressBodySchema,
});
/**
 * Safety between partners, asked of each partner alone when the session goes to Couples (60 A).
 * Each answer is written on that partner's own note; Not a partner skips them (71 A). With no Yes
 * the session becomes Couples; a Yes keeps it on Family until the Yes is marked reviewed.
 */
export const familySafetySchema = z.object({
    answers: z
        .array(z.object({
        contactId: z.string().uuid(),
        a: z
            .tuple([z.enum(['Yes', 'No']), z.enum(['Yes', 'No']), z.enum(['Yes', 'No'])])
            .optional(),
        notPartner: z.boolean().optional(),
    }))
        .min(1)
        .max(10),
});
/**
 * 70 A: only the identified client came (everyone else on the roster is Not present). That is an
 * individual session: the client's note is handed off to a Progress Note and can't be signed here.
 */
export function familyOnlyClientCame(attendees) {
    const roster = attendees.filter((a) => a.onRoster);
    const client = roster.find((a) => a.billed);
    const others = roster.filter((a) => !a.billed);
    return (!!client &&
        client.attendance !== 'Not present' &&
        others.length > 0 &&
        others.every((a) => a.attendance === 'Not present'));
}
//# sourceMappingURL=family-notes.js.map